import React, { useState } from 'react';

import { useDispatch } from 'react-redux';

import Box from '@amzn/meridian/column';
import Text from '@amzn/meridian/text';
import ConfirmModal from 'src/components/shared/confirmModal/confirmModal';
import { setJmrResults, setPageProgress } from 'src/store/newJmrPageSlice/newJmrPageSlice';
import { useAppSelector } from 'src/store/store';
import { JmrFlowTypes, JmrPageProgress } from 'src/utils/jmrUtils';
import {
  getRestrictedValueFull,
  isTimeRestrictionTask,
  isWeightRestrictionParentTask,
  isYesNoRestrictionTask,
  type RestrictionListItem,
} from 'src/utils/restrictionUtils';

import { type ItemProps, SectionTitle, SectionWrapper } from '../common';

/**
 * Default component.
 */
const RestrictionsReview = () => {
  const dispatch = useDispatch();
  const { backgroundFormData, restrictionListItems, jmrFlowType } = useAppSelector((state) => state.newJmrPage);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { shiftLength } = backgroundFormData;

  return (
    <Box spacing="none" spacingInset="none">
      {/* Header */}
      <SectionTitle
        label="Restrictions"
        editButtonDisabled={jmrFlowType === JmrFlowTypes.AlternativePlacementRecommended}
        editButtonOnClick={() => setIsModalOpen(true)}
      />

      {/* Task List */}
      {restrictionListItems.length > 0 ? (
        restrictionListItems.map((task, i) => (
          <Box key={i} spacingInset="none none 300 none">
            <SectionWrapper label={'Restriction ' + (i + 1)} items={setupRestrictionDisplayItems(task, shiftLength)} />
          </Box>
        ))
      ) : (
        <Text type="b300" color="secondary">
          No tasks selected
        </Text>
      )}

      {/* Modals */}
      <ConfirmModal
        width="419px"
        modalTitle="Leave page"
        modalDescription="Leaving this page will restart your Job Match Results process."
        open={isModalOpen}
        setOpen={() => setIsModalOpen(false)}
        onConfirm={() => {
          dispatch(setPageProgress(JmrPageProgress.Restrictions));
          dispatch(setJmrResults([]));
        }}
      />
    </Box>
  );
};

/**
 * Setup the objects to dispay based on the Task type.
 */
const setupRestrictionDisplayItems = (task: RestrictionListItem, shiftLength: number): ItemProps[] => {
  // Add the label
  let retVal: ItemProps[] = [
    {
      label: 'Task',
      value: task.label,
    },
  ];
  // Add the body location value
  if (task.identifyBodyLocation) {
    retVal.push({
      label: 'Body Location',
      value: task.bodyLocationValue,
    });
  }
  // If weight restriction parent task
  if (isWeightRestrictionParentTask(task)) {
    retVal.push({
      label: 'Maximum Weight',
      value: `${task.weightRestrictionMaxRange} lbs`,
    });
    task.children.forEach((child) => {
      if (child.weightRestriction.maxRange <= task.weightRestrictionMaxRange) {
        retVal.push({
          label: child.weightRestriction.header,
          value: getRestrictedValueFull(child, shiftLength),
        });
      }
    });
    // Is time restriction task
  } else if (isTimeRestrictionTask(task)) {
    retVal.push({
      label: 'Maximum Time',
      value: getRestrictedValueFull(task),
    });
    // YES/NO restriction task
  } else if (isYesNoRestrictionTask(task)) {
    retVal = [
      {
        label: 'Task',
        value: `${task.label} - NO`,
      },
    ];
  }
  // Return
  return retVal;
};

export default RestrictionsReview;
